import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Award, Users, Globe, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useLanguageStore, getLocalized, useAppearanceStore } from '../store';
import { useTranslation } from '../i18n/translations';
import { resolveSlot } from '../lib/appearance';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fallback = {
  hero: {
    eyebrow: { en: 'Our Story' },
    headline: { en: 'Architectural interiors, crafted in Europe.' },
    subheadline: { en: 'From a small atelier to one of Europe’s most respected bespoke studios.' },
    image_url: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1920&q=80',
  },
  philosophy: { heading: { en: 'Philosophy' }, body: { en: 'Quiet design. Honest materials. Architectural precision.' } },
  journey: { heading: { en: 'Our Journey' }, body: { en: 'Founded with a vision to transform interior spaces.' } },
  factory: {
    heading: { en: 'The Atelier' },
    body: { en: 'Our 7,000 sqm production facility blends master joiners with CNC precision.' },
    image_url: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80',
    metrics: [
      { value: '7,000', unit: 'sqm', label: { en: 'Production facility' } },
      { value: '15', unit: '+', label: { en: 'Years of excellence' } },
      { value: '6', unit: '', label: { en: 'Countries served' } },
      { value: '50', unit: '+', label: { en: 'Design partners' } },
    ],
  },
  mission: { 
    heading: { en: 'Mission' }, 
    body: { en: 'Quiet luxury, lasting craftsmanship.' },
    subbody: { en: 'We dedicate our practice to creating interior spaces that resonate with structural permanence, functional elegance, and quiet sophistication.' }
  },
  cta: { heading: { en: 'Ready to Start Your Project?' }, body: { en: 'Let’s create something extraordinary together.' } },
};

const VALUE_ICONS = [Award, Target, Users, Globe];

const tx = (v, lang) => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  const localized = getLocalized(v, lang);
  if (typeof localized === 'object' && localized !== null) {
    const firstStr = Object.values(localized).find((s) => typeof s === 'string');
    return firstStr || '';
  }
  return String(localized || '');
};

export const AboutPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/about-us`);
        if (!res.ok) throw new Error();
        const d = await res.json();
        if (alive) setData(d);
      } catch {
        if (alive) setData(fallback);
      }
    })();
    return () => { alive = false; };
  }, []);

  const content = data || fallback;
  const hero = content.hero || fallback.hero;
  const philosophy = content.philosophy || fallback.philosophy;
  const journey = content.journey || fallback.journey;
  const factory = content.factory || fallback.factory;
  const mission = content.mission || fallback.mission;
  const cta = content.cta || fallback.cta;
  const metrics = (factory.metrics && factory.metrics.length > 0) ? factory.metrics : fallback.factory.metrics;
  
  // 🔥 ИСПРАВЛЕНО: Безопасный доступ к картинкам темы
  const siteImages = useAppearanceStore((s) => s.config?.images || {});
  const heroImg = resolveSlot(siteImages, 'about_hero', hero.image_url || fallback.hero.image_url);
  const factoryImg = resolveSlot(siteImages, 'about_factory', factory.image_url || fallback.factory.image_url);

  return (
    <div className="min-h-screen select-none" data-testid="about-page">
      <SEO title={tx(hero.headline, language) || tx(t?.about?.title, language) || 'About'} description={tx(hero.subheadline, language)} path="about" />

      {/* HERO */}
      <section className="relative h-[70vh] overflow-hidden" data-testid="about-hero">
        <img src={heroImg} alt={tx(hero.headline, language)} className="w-full h-full object-cover pointer-events-none" draggable={false} />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-end pb-24">
          <div className="container-custom">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl text-white">
              {tx(hero.eyebrow, language) && <p className="text-caption text-white/60 mb-4">{tx(hero.eyebrow, language)}</p>}
              {tx(hero.headline, language) && <h1 className="text-display">{tx(hero.headline, language)}</h1>}
              {tx(hero.subheadline, language) && <p className="text-body-lg text-white/80 mt-4 max-w-xl">{tx(hero.subheadline, language)}</p>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy + Journey */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} data-testid="about-philosophy">
              {tx(philosophy.heading, language) && <p className="text-caption text-muted-foreground mb-3">{tx(philosophy.heading, language)}</p>}
              <h2 className="text-h1 mb-6">{tx(journey.heading, language) || 'Our Story'}</h2>
              {tx(philosophy.body, language) && <p className="text-body-lg mb-6 whitespace-pre-line">{tx(philosophy.body, language)}</p>}
              {tx(journey.body, language) && <p className="text-body-lg whitespace-pre-line">{tx(journey.body, language)}</p>}
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative" data-testid="about-factory">
              <div className="aspect-[4/5] overflow-hidden bg-neutral-100">
                <img src={factoryImg} alt={tx(factory.heading, language) || 'Atelier'} className="w-full h-full object-cover pointer-events-none" draggable={false} />
              </div>
              {metrics[0] && (
                <div className="absolute -bottom-8 -left-8 bg-foreground text-background p-8 max-w-xs">
                  <p className="text-4xl font-heading font-medium mb-2">{metrics[0].value}{metrics[0].unit}</p>
                  <p className="text-sm text-background/70">{tx(metrics[0].label, language)}</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Factory body */}
      {tx(factory.body, language) && (
        <section className="section-padding bg-surface">
          <div className="container-custom max-w-3xl text-center">
            <p className="text-caption text-muted-foreground mb-4">{tx(factory.heading, language) || 'The Atelier'}</p>
            <p className="text-body-lg whitespace-pre-line">{tx(factory.body, language)}</p>
          </div>
        </section>
      )}

      {/* Metrics */}
      <section className="section-padding bg-foreground text-background" data-testid="about-metrics">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {metrics.map((stat, index) => {
              const Icon = VALUE_ICONS[index % VALUE_ICONS.length];
              return (
                <motion.div key={`metric-${index}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} data-testid={`stat-${index}`}>
                  <Icon className="w-6 h-6 mb-4 mx-auto opacity-70" strokeWidth={1.2} />
                  <p className="text-5xl font-heading font-medium mb-2">{stat.value}{stat.unit}</p>
                  <p className="text-sm text-background/60">{tx(stat.label, language)}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MISSION */}
      {(tx(mission.heading, language) || tx(mission.body, language) || tx(mission.subbody, language)) && (
        <section className="section-padding bg-background">
          <div className="container-custom max-w-5xl text-center space-y-6">
            {tx(mission.heading, language) && (
              <p className="text-caption text-muted-foreground tracking-[0.25em] uppercase text-[11px] font-semibold text-gray-400">
                {tx(mission.heading, language)}
              </p>
            )}
            
            {tx(mission.body, language) && (
              <h2 className="text-display md:text-5xl font-extralight tracking-tight text-gray-900 leading-[1.15] whitespace-pre-line">
                {tx(mission.body, language)}
              </h2>
            )}

            {tx(mission.subbody, language) && (
              <p className="text-sm font-light text-muted-foreground text-gray-500 max-w-xl mx-auto leading-relaxed pt-2 whitespace-pre-line">
                {tx(mission.subbody, language)}
              </p>
            )}
          </div>
        </section>
      )}

      {/* CTA */}
      {(tx(cta.heading, language) || tx(cta.body, language)) && (
        <section className="section-padding bg-surface">
          <div className="container-custom text-center">
            {tx(cta.heading, language) && <h2 className="text-h1 mb-6">{tx(cta.heading, language)}</h2>}
            {tx(cta.body, language) && <p className="text-body-lg mb-8 max-w-xl mx-auto whitespace-pre-line">{tx(cta.body, language)}</p>}
            <Link to={`/${language}/contact`}>
              <Button className="btn-primary" data-testid="about-cta">
                Get in Touch
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default AboutPage;
